import React, {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import _debounce from 'lodash/debounce';
import { IconButton } from '@picsio/ui';
import { DrawFace } from '@picsio/icons';

import picsioConfig from '../../../../../../../config';
import { calculateAiCredits } from '../../../../helpers/calculateAICredits';
import localization from '../../../../shared/strings';
import Logger from '../../../../services/Logger';
import Tooltip from '../../../Tooltip/Tooltip';
import { showDialog } from '../../../dialog';
import AiIcon from '../AiIcon';
import styles from './ExpandDescriptionAction.module.scss';

const { isMainApp, isPim } = picsioConfig;

const ExpandDescriptionAction = (props) => {
  const user = useSelector((state) => state.user);
  const {
    isTitleShow,
    isDescriptionShow,
    disabled,
    actions,
    asset,
    selectedAssetsAggregatedData,
  } = props;

  const titleRef = useRef(selectedAssetsAggregatedData?.title || '');
  const descriptionRef = useRef(selectedAssetsAggregatedData?.description || '');
  const assetId = asset._id;

  const onGenerateDescription = useCallback(() => {
    actions.generateDescription({ assetsIds: [assetId] });
  }, [actions, assetId]);

  const debouncedUpdateRef = useRef(
    _debounce(({ value, ref }) => {
      // eslint-disable-next-line no-param-reassign
      ref.current = value;
    }, 50),
  ).current;

  useEffect(() => {
    if (titleRef.current !== selectedAssetsAggregatedData?.title) {
      titleRef.current = selectedAssetsAggregatedData?.title || '';
    }
    if (descriptionRef.current !== selectedAssetsAggregatedData?.description) {
      descriptionRef.current = selectedAssetsAggregatedData?.description || '';
    }
  }, [selectedAssetsAggregatedData?.title, selectedAssetsAggregatedData?.description]);

  const onTitleChange = useCallback((e) => {
    debouncedUpdateRef({ value: e.target.value, ref: titleRef });
  }, [debouncedUpdateRef]);

  const onDescriptionChange = useCallback((e) => {
    debouncedUpdateRef({ value: e.target.value, ref: descriptionRef });
  }, [debouncedUpdateRef]);

  const onCloseModal = useCallback(async () => {
    Logger.log('User', 'InfoPanelDescriptionFullViewModalClosed');

    if (selectedAssetsAggregatedData?.title !== titleRef.current) {
      await actions.changeTitle([assetId], titleRef.current);
      Logger.log('User', 'InfoPanelChangeTitle', { initiation_location: 'full view modal', selectedAssetsIds: [assetId] });
    }
    if (selectedAssetsAggregatedData?.description !== descriptionRef.current) {
      await actions.changeDescription([assetId], descriptionRef.current);
      Logger.log('User', 'InfoPanelChangeDesc', { initiation_location: 'full view modal', selectedAssetsIds: [assetId] });
    }
  }, [selectedAssetsAggregatedData?.title, selectedAssetsAggregatedData?.description, actions, assetId]);

  const isMetadataEditable = useMemo(
    () => !selectedAssetsAggregatedData?.isRestricted
      || (selectedAssetsAggregatedData?.isRestricted && selectedAssetsAggregatedData?.permissions?.restrictedMetadataEditable),
    [selectedAssetsAggregatedData],
  );

  const onOpenExpandModal = useCallback(() => {
    Logger.log('User', 'InfoPanelDescriptionFullViewModalShown');
    const describingIsAvailable = user.subscriptionFeatures?.describing && !isPim;
    const credits = calculateAiCredits(user.team);
    const inProgressDescription = ['running', 'waiting'].includes(asset?.describing);
    const isAllowShowTitle = selectedAssetsAggregatedData?.permissions?.titleShow && isTitleShow;
    const isAllowShowDescription = selectedAssetsAggregatedData?.permissions?.descriptionShow && isDescriptionShow;
    const isAllowEditTitle = selectedAssetsAggregatedData?.permissions?.titleEditable && isMetadataEditable;
    const isAllowEditDescription = selectedAssetsAggregatedData?.permissions?.descriptionEditable && isMetadataEditable;
    const isTitleDisabled = disabled || !isAllowEditTitle;
    const isDescriptionDisabled = disabled || inProgressDescription || !isAllowEditDescription;

    const input = isAllowShowTitle ? {
      input: {
        value: titleRef.current,
        readOnly: !isAllowEditTitle,
        autoFocus: !isAllowEditTitle,
        disabled: isTitleDisabled,
        placeholder: localization.DETAILS.placeholderEnterTitle,
        onChange: onTitleChange,
      },
    } : {};

    const onCloseDialog = showDialog({
      title: localization.DETAILS.textTitleAndDescription,
      text: localization.DETAILS.textTitleAndDescriptionText,
      ...input,
      ...(isAllowShowDescription && {
        textarea: {
          readOnly: !isAllowEditDescription,
          className: styles.expandDescriptionArea,
          value: descriptionRef.current,
          disabled: isDescriptionDisabled,
          placeholder: isMainApp ? localization.DETAILS.placeholderAIDescription : localization.DETAILS.placeholderEnterDescription,
          height: '300',
          onResize: null,
          onChange: onDescriptionChange,
          icon: (
            <If condition={isMainApp && asset?.isImage && !!asset?.permissions?.autogenerateDescriptions && !isPim}>
              <AiIcon
                className={styles.expandDescriptionSparks}
                disabled={isDescriptionDisabled}
                data-testid="iconGenerateDescription"
                tooltipText={!describingIsAvailable || !credits ? localization.DETAILS.aiIconUpdatePlanTooltipText('Generate descriptions.') : localization.DETAILS.aiIconTooltipText(inProgressDescription ? localization.DETAILS.describingBtnText : localization.DETAILS.generateDescriptionBtnText, credits, 'text-center')}
                inProgress={inProgressDescription}
                onSubmit={() => {
                  Logger.log('User', 'InfoPanelDescriptionAutogenerate', { initiation_location: 'full view modal' });
                  onGenerateDescription();
                  onCloseDialog();
                }}
                size="lg"
              />
            </If>
          ),
        },
      }),
      buttonOkColor: 'primary',
      textBtnOk: null,
      textBtnCancel: null,
      isCloseButtonShown: true,
      onCancel: onCloseModal,
    });
  }, [
    isTitleShow,
    isDescriptionShow,
    user.subscriptionFeatures?.describing,
    user.team,
    asset?.describing,
    asset?.isImage,
    asset?.permissions?.autogenerateDescriptions,
    selectedAssetsAggregatedData?.permissions?.titleShow,
    selectedAssetsAggregatedData?.permissions?.descriptionShow,
    selectedAssetsAggregatedData?.permissions?.titleEditable,
    selectedAssetsAggregatedData?.permissions?.descriptionEditable,
    isMetadataEditable,
    disabled,
    onTitleChange,
    onDescriptionChange,
    onCloseModal,
    onGenerateDescription,
  ]);

  return (
    <IconButton
      disabled={disabled}
      className={styles.expandIcon}
      componentProps={{ 'data-testid': 'actionDescriptionExpand' }}
      size="lg"
      onClick={!disabled && onOpenExpandModal}
    >
      <Tooltip placement="left" content={localization.DETAILS.tooltipExpandIconDescription}>
        <div>
          <DrawFace />
        </div>
      </Tooltip>
    </IconButton>
  );
};

ExpandDescriptionAction.propTypes = {
  isTitleShow: PropTypes.bool,
  isDescriptionShow: PropTypes.bool,
  disabled: PropTypes.bool,
  asset: PropTypes.shape({
    _id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    isImage: PropTypes.bool,
    describing: PropTypes.string,
    permissions: PropTypes.shape({
      autogenerateDescriptions: PropTypes.bool,
    }),
  }).isRequired,
  actions: PropTypes.objectOf(PropTypes.func).isRequired,
  selectedAssetsAggregatedData: PropTypes.shape({
    isRestricted: PropTypes.bool,
    titleShow: PropTypes.bool,
    descriptionShow: PropTypes.bool,
    titleEditable: PropTypes.bool,
    descriptionEditable: PropTypes.bool,
    title: PropTypes.string,
    description: PropTypes.string,
    permissions: PropTypes.shape({
      titleShow: PropTypes.bool,
      descriptionShow: PropTypes.bool,
      titleEditable: PropTypes.bool,
      descriptionEditable: PropTypes.bool,
      restrictedMetadataEditable: PropTypes.bool,
    }),
  }),
};

ExpandDescriptionAction.defaultProps = {
  isTitleShow: true,
  isDescriptionShow: true,
  disabled: false,
  selectedAssetsAggregatedData: {},
};

export default ExpandDescriptionAction;
